import { tracker } from 'app-components/utils/core';
import { DateTime } from 'luxon';
// CORE
import React, { useRef, useState } from 'react';

import { preferencesV2Actions, preferencesV2Selectors, preferencesV2Types } from 'app-components/flux/preferences-v2';
import { transactionsActions, transactionsSelectors } from 'app-components/flux/transactions';
import { useDispatch, useSelector } from 'react-redux';

import { CircularProgress, FormControl, InputAdornment, InputLabel, MenuItem, Select, useTheme } from '@mui/material';
import { dateSelectors } from 'app-components/flux/date';
import { makeStyles } from 'tss-react/mui';

type RegisterOnAfterDateProps = {
  label?: string;
  overrideText?: string;
  rootClassName?: string;
  /** Will the selection affect the global preference */
  temporarySelection?: boolean;
  /** Mixpanel prop for initiator */
  initiator?: string;
  controlled?: boolean;
  onChange?: (dateIso: string | null) => void;
};

const useStyles = makeStyles()((theme) => ({
  last: {
    '& div:hover': {
      backgroundColor: `${theme.palette.action.hover} !important`,
    },
    backgroundColor: `${theme.palette.background.paper} !important`,
    borderRadius: 12,
    width: '100%',
  },
}));

function RegisterOnAfterDate({
  label,
  rootClassName,
  overrideText,
  temporarySelection,
  initiator,
  onChange,
  controlled = true,
}: RegisterOnAfterDateProps) {
  const { classes } = useStyles();
  const theme = useTheme();
  const dispatch = useDispatch();
  const ref = useRef<HTMLDivElement>(null);

  const isLoading = useSelector(transactionsSelectors.getIsLoading);
  const dateOnAfterISO: string = useSelector(
    temporarySelection
      ? transactionsSelectors.getDateOnAfter
      : (state) => preferencesV2Selectors.getPreferenceValue(state, 'dateOnAfter'),
  );
  const minDateISO = useSelector(transactionsSelectors.getEarliestDateOn);
  const today = useSelector(dateSelectors.getCurrentDate);

  const minDateYear = minDateISO ? DateTime.fromISO(minDateISO).year : null;
  const yearOnAfter = DateTime.fromISO(dateOnAfterISO).plus({ days: 1 }).year; // plus one because we minus one when sent to QCS

  const diff = minDateYear ? today.year - minDateYear : 0;
  const isAllTime = !minDateISO || dateOnAfterISO < minDateISO;

  const val = isAllTime ? 'All' : yearOnAfter;
  const [initialVal] = useState(isAllTime ? 'All' : yearOnAfter);

  return yearOnAfter && minDateISO ? (
    <FormControl>
      <InputLabel id='select-label-after-date'>{label}</InputLabel>
      <Select
        id={'after_date_year_dropdown'}
        className={rootClassName}
        labelId='select-label-after-date'
        ref={ref}
        variant={'outlined'}
        label={label}
        size='small'
        IconComponent={isLoading ? () => null : undefined}
        name={'yearOnAfter'}
        value={controlled ? val : undefined}
        defaultValue={!controlled ? initialVal : undefined}
        onChange={(e) => {
          const year = e.target.value;
          const date = year === 'All' ? DateTime.fromISO(minDateISO) : DateTime.local(Number(year), 1, 1);
          const dateIso = date.minus({ days: 1 }).toISODate();
          if (dateIso && onChange) {
            onChange?.(dateIso);
            if (controlled) {
              tracker.track(tracker.events.transactionsOnDemand, {
                selectionType: temporarySelection ? 'temporary' : 'preference',
                date: dateIso,
                earliestDate: minDateISO,
                initiator,
              });
              if (!temporarySelection) {
                dispatch(
                  preferencesV2Actions.updatePreferenceOverride(
                    preferencesV2Types.mkPreferenceV2({
                      id: 'dateOnAfter',
                      value: dateIso,
                    }),
                  ),
                );
              }
              dispatch(transactionsActions.getTransactions(undefined, { props: { dateOnAfter: dateIso } }));
            }
          } else if (onChange) {
            onChange(null);
          }
          ref.current?.blur();
        }}
        renderValue={(year) => overrideText || (year === 'All' ? `All Time (Since ${minDateYear})` : `Since ${year}`)}
        endAdornment={
          isLoading ? (
            <InputAdornment position='start' sx={{ position: 'absolute', right: 4 }}>
              <CircularProgress size={16} />
            </InputAdornment>
          ) : null
        }
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 300,
              width: 200,
              paddingRight: 8,
            },
            sx: {
              '& .MuiList-root': {
                paddingBottom: 0,
              },
              '& .MuiMenuItem-root[data-value="All"] ': {
                position: 'sticky',
                borderRadius: 0,
                borderTop: `1px solid ${theme.palette.border.light}`,
                backgroundColor: theme.palette.background.paper,
                bottom: 0,
                height: 48,
                mt: 1,
                pt: 1,
                pb: 1,
                px: 0,
                '& div': {
                  width: '100%',
                  borderRadius: 2,
                  px: 2,
                },
              },
              '& .MuiMenuItem-root[data-value="All"]:hover,  & .Mui-selected': {
                backgroundColor: theme.palette.background.paper,
                pt: 1,
                pb: 1,
                '& div': {
                  backgroundColor: theme.palette.action.hover,
                  mt: 0,
                  py: 1,
                },
              },
            },
          },
        }}
      >
        {Array(diff)
          .fill(0)
          .map((_val, index) => (
            <MenuItem key={`target-year-${today.year - index}`} value={today.year - index}>
              {`Since ${today.year - index}`}
            </MenuItem>
          ))}
        <MenuItem key={'target-year-all'} value={'All'} className={classes.last}>
          <div>{`All Time (Since ${minDateYear})`}</div>
        </MenuItem>
      </Select>
    </FormControl>
  ) : null;
}

export default React.memo(RegisterOnAfterDate);
